/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type KeyboardEvent, type ReactElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { cn } from '@/lib/utils';
import { defaultSortId, resolveSortOptions, selectSortOption, type SortOption } from '@/lib/search/sort-options';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/**
 * BG's SRP/PLP sort control — a BORDERLESS TEXT TRIGGER plus a chevron that
 * opens BG'S OWN IN-DOCUMENT PANEL. It is a different component from NM's
 * bordered native select, not a re-skin of it.
 *
 * WHAT LIVE BG RENDERS. From the raw server-rendered dump
 * `.reference/bg-search-dress.desktop.html` (the captures' decompiler strips
 * class names, so the dump is the only valid source for the markup):
 *
 *   div.product-list-info.desktop-tablet-only          <- 1 occurrence, control 2604 `class="`
 *     span.main-item-count                             "3,726 Results"
 *     div > div.product-list__header__sort.hide-on-mobile
 *       div.product-list__header__sort__label > span   "Sort By"
 *       span.product-list__header__sort__dropdown_wrapper > span
 *         button.sort-label
 *           span                                       "Best Match"
 *           img[alt=chevron]                           16x16 inline SVG
 *         div.sort-container                           <- the open panel, below
 *
 * No `<select>` anywhere in the file — `sort-label` occurs once and `<option>`
 * zero times. Trigger geometry is read off BG's own captures, where the two
 * desktop widths carry byte-identical class strings
 * (bg-search-d-1280/App.tsx:2212-2224, bg-search-d-1024/App.tsx:256-268):
 * label and value both `text-[14px] leading-[19.32px]`, `mr-[10px]` after the
 * label, `mr-[7px]` before the chevron, value `text-black` inside a row that is
 * `text-[rgb(101,101,101)]`. Values land as `--srp-sort-menu-*` in
 * `theme/tokens/brand-bg/search.css`; the rules that consume the panel half of
 * that set are in `theme/overrides/search.css`.
 *
 * THE PANEL USED TO BE A NATIVE `<select>` AND CANNOT BE ONE. A native select's
 * popup is drawn by the OS outside the document, so no rule could ever reach
 * it: measured on the previous build, the trigger read `SELECT` with opacity 0
 * and clicking it put ZERO nodes in the DOM. Every value below was therefore
 * unlandable. The panel is real markup now, which is the only thing that makes
 * them reachable.
 *
 * THE PANEL'S SPEC IS DECLARED, NOT PHOTOGRAPHED, and that is a stronger tier
 * than a capture. `sort-container` returns 0 in every at-rest dump — the panel
 * is only in the DOM while open — but live's shipped stylesheet declares the
 * open state outright. A brace-depth walk of
 * `.reference/dumps/bg-search-m-440/mob-search-440_files/client.08f7ab726bd774a425bc.bundle.css`
 * parses 7890 rules and returns 35 whose selector contains `sort`; the family
 * is `.sort-container`, so a query for the vocabulary we use (sort + dropdown /
 * menu / option / select) returns one rule and reads as "no evidence". The full
 * enumeration and the derived values are recorded in
 * `theme/tokens/brand-bg/search.css` at the SORT CONTROL entry.
 *
 * TWO RULES ON THIS SURFACE INVERT THE BASE ONE, and missing them would put the
 * panel on the wrong side of the control with no border. The control lives
 * inside `div.product-list-info`, so the panel mounts inside it too, and
 * `.product-list-info .sort-container` at (0,2,0) beats both the top-level
 * `border:none` and the >=768 `right:-30px`: the panel is BORDERED and
 * LEFT-anchored. The structure below therefore hangs the panel off THIS
 * component's root — live's positioned ancestor is `.product-list__header__sort`,
 * the block that holds the "Sort By" label AND the trigger, which is this
 * component's root box, so live's `top:42px; left:0` is measured from here.
 *
 * WHAT WE RENDER, and why the nesting is live's rather than the flatter thing:
 *
 *   div          root, `relative`                 = .product-list__header__sort
 *     span       "Sort By"
 *     button     trigger, aria-haspopup/-expanded = button.sort-label
 *     div        panel                            = div.sort-container
 *       ul       role=menu
 *         li     role=none
 *           button role=menuitemradio             = live's `ul li button`
 *
 * `li` and `button` are NOT collapsed into one element. Live's option underline
 * sits on the BUTTON (`border-bottom:1px solid #fff`, recoloured to #000 when
 * active or hovered), so it hugs the option's own text; the `li` is a block as
 * wide as the panel's content box, so an underline moved onto it would run the
 * width of the LONGEST option under every row.
 *
 * THE HOVER MECHANISM IS A NO-REFLOW UNDERLINE and is the part no screenshot
 * could have shown: every option already carries a WHITE 1px bottom border,
 * invisible against the white panel, and hover/selected only change its colour.
 * Nothing moves. Adding a border on hover instead would shift every row by 1px.
 *
 * ACCESSIBILITY IS THE REASON THIS IS A MENU AND NOT A DIV WITH CLICK HANDLERS.
 * A native `<select>` is accessible for free and a custom panel is not, so the
 * replacement has to earn it back: APG menu-button semantics, roving tabindex,
 * arrow/Home/End movement, first-character type-ahead (which native selects
 * have and a bare listbox does not), Enter/Space to choose, Escape to close and
 * return focus to the trigger. `menuitemradio` rather than `menuitem` because
 * the options are one mutually-exclusive choice, which is what a sort is.
 *
 * FOCUS IS CONTAINED WHILE OPEN — Tab and Shift+Tab move between options rather
 * than leaving the panel. That is deliberately stricter than APG's menu button
 * (where Tab closes and moves on). Escape is the unconditional way out and
 * always returns focus to the trigger, so this is containment, not a WCAG 2.1.2
 * keyboard trap. Clicking away, or focus reaching outside the root by any other
 * route, closes the panel without stealing focus back.
 *
 * MOBILE KEEPS ITS OWN CONTROL AND THIS COMPONENT NEVER RENDERS THERE. Both
 * mounts (`bg-listing-toolbar.tsx`, `routes/_app.search.tsx`) wrap it in
 * `hidden md:flex`, which is live's own `desktop-tablet-only` on
 * `div.product-list-info`. Live's mobile sort is a different control again — the
 * mobile dump carries `div.mobile-options-container.hide-on-desktop.hide-on-tablet`
 * with a `Filters | Sort` row whose Sort button opens a FIXED FULL-SCREEN
 * `.mobile-overlay__pane` (inset 0, background #fff, z-index 1010, centred
 * uppercase h2 header, 30x30 close at the leading edge). That is a modal, not
 * this panel, and it is not built here: our mobile sort stays on the native
 * select in `bg-filter-sort-bar.tsx`.
 *
 * URL-driven like NM's control (`?sort=<id>` through the search loader's SCAPI
 * `sort` pass-through) with the pagination params reset.
 */
export default function SortMenu({
    serverOptions,
    fallbackOptions,
    className,
}: {
    serverOptions?: Array<{ id?: string; label?: string }>;
    /** The brand's curated list — named by the caller, never defaulted. */
    fallbackOptions: readonly SortOption[];
    className?: string;
}): ReactElement {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    const options = resolveSortOptions(serverOptions, fallbackOptions);

    // Optimistic: read the in-flight target's sort while a navigation pends.
    const currentSort =
        (navigation.location
            ? new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)
            : new URLSearchParams(location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)) ?? defaultSortId(options);

    const labelOf = useCallback(
        (option: (typeof options)[number]) =>
            option.label ??
            (option.labelKey ? t(option.labelKey, { defaultValue: option.defaultLabel ?? option.id }) : option.id),
        [t]
    );

    // `selectSortOption`, NOT `find(...) ?? options[0]`. BG's list has no
    // `top-sellers`, so an NM-only id in the URL missed and position 0 displayed
    // "Price: high to low" on a page sorted otherwise. See its docblock.
    const selected = selectSortOption(options, currentSort);
    const selectedIndex = Math.max(
        0,
        options.findIndex((option) => option.id === selected?.id)
    );

    const baseId = useId();
    const labelId = `${baseId}-label`;
    const valueId = `${baseId}-value`;
    const panelId = `${baseId}-panel`;

    const [open, setOpen] = useState(false);
    // Roving tabindex: exactly one option is reachable by Tab at a time, and it
    // is the one that has DOM focus.
    const [activeIndex, setActiveIndex] = useState(selectedIndex);

    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const closeMenu = useCallback((returnFocus: boolean) => {
        setOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    }, []);

    const openMenu = useCallback(
        (index: number) => {
            setActiveIndex(index);
            setOpen(true);
        },
        [setActiveIndex]
    );

    // Focus follows `activeIndex` while open. Written as one effect rather than
    // a `.focus()` beside every key handler so there is a single place where
    // "the active option is the focused option" can be true or false.
    useEffect(() => {
        if (!open) return;
        optionRefs.current[activeIndex]?.focus();
    }, [open, activeIndex]);

    // Click-away. `pointerdown` rather than `click` so the panel is gone before
    // the pressed element runs its own handler, and capture so a child that
    // stops propagation cannot keep the panel open.
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: Event): void => {
            const target = event.target as Node | null;
            if (target && rootRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);

    const onChange = useCallback(
        (sort: string) => {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
            // Reset paging — both the legacy offset and the crawlable page param.
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            params.delete('page');
            void navigate({ ...location, search: `?${params.toString()}` });
        },
        [location, navigate]
    );

    const choose = useCallback(
        (sort: string) => {
            closeMenu(true);
            onChange(sort);
        },
        [closeMenu, onChange]
    );

    const sortByLabel = t('sort.labelBare', { defaultValue: 'Sort By' });

    const onTriggerKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
            // Enter and Space already fire `click` on a <button>; only the
            // arrows need intercepting. Both open on the SELECTED row rather
            // than on first/last, which is what a native select does and what
            // this control replaced.
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                openMenu(selectedIndex);
            }
        },
        [openMenu, selectedIndex]
    );

    const onOptionKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
            const last = options.length - 1;
            const step = (delta: number): void => {
                event.preventDefault();
                setActiveIndex((index + delta + options.length) % options.length);
            };

            switch (event.key) {
                case 'ArrowDown':
                    return step(1);
                case 'ArrowUp':
                    return step(-1);
                case 'Home':
                    event.preventDefault();
                    return setActiveIndex(0);
                case 'End':
                    event.preventDefault();
                    return setActiveIndex(last);
                case 'Tab':
                    // Contained, not closed — see the docblock. Escape is the
                    // way out and it always restores the trigger.
                    return step(event.shiftKey ? -1 : 1);
                case 'Escape':
                    event.preventDefault();
                    return closeMenu(true);
                default:
                    break;
            }

            // Type-ahead. A native select jumps to the next option starting with
            // the typed character; a menu that dropped that would be a
            // regression on the control it replaced, not parity with it.
            if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
                const char = event.key.toLowerCase();
                for (let hop = 1; hop <= options.length; hop += 1) {
                    const candidate = (index + hop) % options.length;
                    const option = options[candidate];
                    if (option && labelOf(option).toLowerCase().startsWith(char)) {
                        event.preventDefault();
                        setActiveIndex(candidate);
                        return;
                    }
                }
            }
        },
        [closeMenu, labelOf, options]
    );

    return (
        <div
            ref={rootRef}
            className={cn(
                // `relative` is live's `.product-list__header__sort{position:relative}`
                // and is what the panel's `top:42px; left:0` is measured from.
                //
                // The tracking lands HERE because live declares it here too:
                // `.product-list__header__sort{letter-spacing:.4px}` is the only
                // (0,1,0) rule carrying it, and the "Sort By" label span inherits
                // from this element. (Live's trigger repeats the same .4px on
                // `button.sort-label`; one declaration on the row covers both
                // spans for us, because nothing in between redeclares it.)
                // Bare custom property on purpose: a brand that declares no value
                // makes this invalid at computed-value time, so the row inherits
                // as it does today rather than picking up BG's number.
                'relative flex items-center tracking-(--srp-sort-menu-row-letter-spacing)',
                isPending && 'pointer-events-none opacity-50 transition-opacity',
                className
            )}
            data-testid="search-sort-menu">
            <span
                id={labelId}
                className="mr-(--srp-sort-menu-label-gap) text-(length:--srp-sort-menu-font-size) leading-(--srp-sort-menu-line-height) whitespace-nowrap">
                {sortByLabel}
            </span>
            {/* The trigger is sized by the SELECTED label exactly as live's
                `button.sort-label` is, and carries no box of its own:
                `.product-list-info .sort-label` sets border-bottom:none and
                padding:0 over the top-level `.sort-label` underline. */}
            <button
                ref={triggerRef}
                type="button"
                data-testid="search-sort-menu-trigger"
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={panelId}
                // "Sort By" + the current value, so the name matches what the
                // native select announced before this replaced it.
                aria-labelledby={`${labelId} ${valueId}`}
                onClick={() => (open ? closeMenu(false) : openMenu(selectedIndex))}
                onKeyDown={onTriggerKeyDown}
                className="focus-ring-button inline-flex cursor-pointer items-center text-(--srp-sort-menu-value-color)">
                <span
                    id={valueId}
                    className="mr-(--srp-sort-menu-value-gap) text-(length:--srp-sort-menu-font-size) leading-(--srp-sort-menu-line-height) whitespace-nowrap">
                    {selected ? labelOf(selected) : ''}
                </span>
                <ChevronDownIcon />
            </button>
            {open && (
                <div data-testid="search-sort-menu-panel">
                    <ul id={panelId} role="menu" aria-labelledby={labelId}>
                        {options.map((option, index) => {
                            const isSelected = option.id === selected?.id;
                            /*
                             * `text-start` on the LI is measured, not decorative, and it
                             * has to live here rather than in the stylesheet.
                             *
                             * The owner's own capture of live BG's OPEN panel
                             * (`live-shots/owner-2026-08-23-sort/
                             * sort-dropdown-options-left-aligned.png`, bergdorfgoodman.com
                             * search for "dress", 3456px wide at DPR 2 = 1728 CSS) shows
                             * all four options sharing ONE LEFT EDGE with four different
                             * right edges — start-aligned.
                             *
                             * The panel rule carries `text-align: right`, read off
                             * `.sort-container` in live's shipped bundle. That reading is
                             * now REFUTED, and by provenance rather than by re-measuring:
                             * it came from `client.08f7ab726bd774a425bc.bundle.css`, while
                             * the fresh 2026-08-23 capture of live BG links
                             * `client.a86b4bec81dc63dafa8e.bundle.css`. The declaration was
                             * read correctly off a stylesheet live no longer ships, so the
                             * photograph outranks it. `.product-list-info .sort-container`
                             * at (0,2,0) — the rule that inverts border and side — declares
                             * no text-align, so nothing else was overriding it.
                             *
                             * WHY THE LI AND NOT THE PANEL. The panel rule is unlayered
                             * (`theme/overrides/search.css`), and the cascade settles layer
                             * order before specificity — so a utility on the PANEL would
                             * land on the element, read correctly in review, and do
                             * nothing. The LI only INHERITS text-align; nothing declares it
                             * there, and an own-element declaration beats an inherited
                             * value with no layer comparison entering it. It also keeps the
                             * change inside this component rather than in a stylesheet that
                             * other surfaces share.
                             */
                            return (
                                <li key={option.id} role="none" className="text-start">
                                    <button
                                        ref={(node) => {
                                            optionRefs.current[index] = node;
                                        }}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={isSelected}
                                        tabIndex={index === activeIndex ? 0 : -1}
                                        data-testid="search-sort-menu-option"
                                        onClick={() => choose(option.id)}
                                        onKeyDown={(event) => onOptionKeyDown(event, index)}>
                                        <span>{labelOf(option)}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

/**
 * Live BG's own chevron, decoded from the base64 `img[alt=chevron]` in
 * `.reference/bg-search-dress.desktop.html` — a 16x16 viewBox with the caret
 * path below. Inlined rather than fetched: the dump's asset is a data URI on
 * live and an external Google-storage URL in the capture, and neither belongs
 * in a bundle.
 */
function ChevronDownIcon(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className="size-(--srp-sort-menu-chevron-size) shrink-0">
            <path d="M8 9.829L4.419 6.667 4 7.086l4 3.58 4-3.58-.419-.42L8 9.83z" fill="currentColor" />
        </svg>
    );
}
